import { useState, useMemo } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { trpc } from '@/lib/trpc';
import { Card, CardHeader, CardTitle, CardContent } from '@deepguide-ai/ui/card';
import { ScrollArea } from '@deepguide-ai/ui/scroll-area';
import { Badge } from '@deepguide-ai/ui/badge';
import { cn } from '@/lib/utils';
import { EvidenceBacklinkDTO } from '@deepguide-ai/types';
import type { ViewerRequirement, ViewerPermissions, ElementScore, EvidencePackViewerDTO } from '@deepguide-ai/types';
import { Image as ImageIcon, MessageSquareText, FileText, Loader2, Check, AlertCircle, X, ChevronDown, ChevronUp } from 'lucide-react';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@deepguide-ai/ui/alert-dialog';


// Requirements Panel Component
export function RequirementsPanel({
  evidencePackId,
  requirements,
  completeness,
  backlinks,
  permissions: _permissions,
  elementScores,
  selectedRequirement,
  selectedBacklinkId,
  onSelectRequirement,
  onBacklinkClick,
  layout="horizontal",
}: {
  evidencePackId: string;
  requirements: ViewerRequirement[];
  completeness: EvidencePackViewerDTO['completeness'];
  backlinks: EvidenceBacklinkDTO[];
  permissions: ViewerPermissions;
  elementScores?: ElementScore[] | null;
  selectedRequirement: string | null;
  selectedBacklinkId?: string | null;
  onSelectRequirement: (id: string | null) => void;
  onBacklinkClick?: (backlink: EvidenceBacklinkDTO) => void;
  layout?: 'horizontal' | 'vertical';
}) {
  const queryClient = useQueryClient();
  const [deleteBacklinkId, setDeleteBacklinkId] = useState<string | null>(null);
  const [expandedReqs, setExpandedReqs] = useState<Set<string>>(new Set());

  const toggleExpanded = (reqId: string) => {
    setExpandedReqs(prev => {
      const next = new Set(prev);
      if (next.has(reqId)) {
        next.delete(reqId);
      } else {
        next.add(reqId);
      }
      return next;
    });
  };

  const deleteMutation = useMutation({
    ...trpc.evidencePack.deleteBacklink.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: trpc.evidencePack.getBacklinks.queryKey({ evidencePackId }),
      });
      setDeleteBacklinkId(null);
    },
  });

  const backlinksByReq = useMemo(() => {
    const map = new Map<string, EvidenceBacklinkDTO[]>();
    backlinks.forEach((bl) => {
      const existing = map.get(bl.requirementId) || [];
      existing.push(bl);
      map.set(bl.requirementId, existing);
    });
    return map;
  }, [backlinks]);

  const handleDeleteBacklink = (backlinkId: string) => {
    deleteMutation.mutate({ backlinkId });
  };

  const isTextResponse = (backlink: EvidenceBacklinkDTO) => {
    if (!backlink.location) return false;
    if ('artifactType' in backlink.location) {
      return (backlink.location as { artifactType: string }).artifactType === 'text_response';
    }
    return false;
  };

  const isClickable = (backlink: EvidenceBacklinkDTO) => {
    return backlink.screenshotId || backlink.evidenceFileId;
  };

  const getBacklinkReasoning = (backlink: EvidenceBacklinkDTO): string | null => {
    if (!elementScores) return null;
    const score = elementScores.find((s) => s.elementId === backlink.requirementId);
    if (!score) return null;
    return score.reasoningText || null;
  };

  const getLocationLabel = (backlink: EvidenceBacklinkDTO) => {
    if (backlink.screenshotId) {
      return 'Screenshot';
    }

    if (backlink.evidenceFileId) {
      const location = backlink.location;
      if (location) {
        if ('page' in location) {
          return `Page ${location.page}${location.paragraph ? `, Para ${location.paragraph}` : ''}`;
        }
        if ('cellRange' in location) {
          return `Cells ${location.cellRange}`;
        }
        if ('lineStart' in location) {
          return `Lines ${location.lineStart}-${location.lineEnd || location.lineStart}`;
        }
        if ('boundingBox' in location) {
          return 'Image region';
        }
      }
      return 'Document';
    }

    if (isTextResponse(backlink)) {
      return 'Text Response';
    }

    const location = backlink.location;
    if (location && 'artifactType' in location) {
      const artifactType = (location as { artifactType: string }).artifactType;
      if (artifactType === 'screenshot') {
        return 'Screenshot';
      }
    }

    return 'Unknown';
  };

  const getIcon = (backlink: EvidenceBacklinkDTO) => {
    if (backlink.screenshotId) {
      return <ImageIcon className="h-4 w-4 text-muted-foreground mt-0.5" />;
    }
    if (isTextResponse(backlink)) {
      return <MessageSquareText className="h-4 w-4 text-muted-foreground mt-0.5" />;
    }
    return <FileText className="h-4 w-4 text-muted-foreground mt-0.5" />;
  };

  return (
    <>
    <Card className='gap-3 shadow-none'>
      <CardHeader className='px-4 py-0'>
        <CardTitle className="flex items-center justify-between text-sm">
          <span>Requirements</span>
          <span className="text-xs font-normal text-muted-foreground">
            {completeness.demonstratedElements}/{completeness.requiredElements} demonstrated
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <ScrollArea className="" viewClassName={`${layout === 'vertical' ? 'max-h-[520px] h-full' : 'h-[520px]'} [&>div]:!block`}>
          <div className="space-y-2 p-4 pt-0">
            {requirements.map((req) => {
              const reqBacklinks = backlinksByReq.get(req.id) || [];
              const isSelected = selectedRequirement === req.id;
              const isExpanded = expandedReqs.has(req.id);

              return (
                <div
                  key={req.id}
                  className="w-full rounded-md text-left"
                >
                  <div
                    className="flex flex-col gap-1 p-2 cursor-pointer"
                    onClick={() => onSelectRequirement(isSelected ? null : req.id)}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium text-sm flex-1">
                        {req.name}
                      </div>
                      {/* {element && <StatusBadge status={element.status} />} */}
                    </div>
                    {req.description && (
                      <div className="text-xs text-muted-foreground">
                        {req.description}
                      </div>
                    )}
                    {reqBacklinks.length > 0 && (
                      <button
                        className="text-xs text-muted-foreground flex items-center gap-1 mt-1 hover:text-foreground transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpanded(req.id);
                        }}
                      >
                        {isExpanded ? (
                          <ChevronUp className="h-3 w-3" />
                        ) : (
                          <ChevronDown className="h-3 w-3" />
                        )}
                        {reqBacklinks.length} link{reqBacklinks.length !== 1 ? 's' : ''}
                      </button>
                    )}
                  </div>
                  {reqBacklinks.length > 0 && isExpanded && (
                    <div className="space-y-1 p-2">
                      {reqBacklinks.map((bl) => {
                        const clickable = isClickable(bl);
                        const reasoning = getBacklinkReasoning(bl);
                        const isSelected = selectedBacklinkId === bl.id;
                        return (
                          <div
                            key={bl.id}
                            className={cn(
                              'flex items-start flex-col gap-1 p-2 rounded-md text-sm bg-muted/30 border border-transparent transition-colors',
                              clickable && !isSelected && 'hover:border-primary/50 hover:bg-muted/50 cursor-pointer',
                              isSelected && 'border-primary bg-primary/15 cursor-pointer'
                            )}
                            onClick={(event) => {
                              event.stopPropagation();
                              if (clickable) {
                                onBacklinkClick?.(bl);
                              }
                            }}
                          >
                            <div className='flex items-center gap-2 w-full'>
                              {getIcon(bl)}
                              <div className="flex-1 min-w-0 text-xs text-muted-foreground">
                                {getLocationLabel(bl)}
                              </div>
                              {bl.confidence && (
                                <Badge
                                  variant="outline"
                                  className="text-xs shrink-0"
                                >
                                  {Math.round(bl.confidence * 100)}%
                                </Badge>
                              )}
                              {/* {permissions.allowComments && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 shrink-0"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setDeleteBacklinkId(bl.id);
                                  }}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              )} */}
                            </div>
                            {bl.matchedText && (
                              <div className="text-xs mt-1 w-full break-words">
                                "{bl.matchedText}"
                              </div>
                            )}
                            {reasoning && (
                              <div className="text-xs mt-1 break-words text-muted-foreground italic">
                                 {reasoning}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {/* {reqBacklinks.length === 0 && isSelected && (
                    <div className="px-2 pb-2 text-xs text-muted-foreground italic">
                      No links yet
                    </div>
                  )} */}
                </div>
              );
            })}
            {requirements.length === 0 && (
              <div className="text-sm text-muted-foreground text-center py-8">
                No requirements defined
              </div>
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
    <AlertDialog open={!!deleteBacklinkId} onOpenChange={() => setDeleteBacklinkId(null)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Backlink</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to remove this backlink? This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => deleteBacklinkId && handleDeleteBacklink(deleteBacklinkId)}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
// Status badge component
export function StatusBadge({ status }: { status: 'demonstrated' | 'partial' | 'missing' }) {
  switch (status) {
    case 'demonstrated':
      return (
        <Badge variant="outline" className="border-status-green/30 bg-status-green-bg text-status-green-strong">
          <Check className="mr-1 h-3 w-3" />
          Demonstrated
        </Badge>
      );
    case 'partial':
      return (
        <Badge variant="outline" className="border-yellow-200 bg-yellow-50 text-yellow-700">
          <AlertCircle className="mr-1 h-3 w-3" />
          Partial
        </Badge>
      );
    case 'missing':
      return (
        <Badge variant="outline" className="border-status-red/30 bg-status-red-bg text-status-red-strong">
          <X className="mr-1 h-3 w-3" />
          Missing
        </Badge>
      );
  }
}
